import {
  arrangementForArtist as domainArrangementForArtist,
  computePayout as domainComputePayout,
  type CommissionBreakdown as DomainCommissionBreakdown,
} from '@atink/domain';
import type { Artist, CommissionArrangement, SaleLine } from '@/domain/types';

/**
 * The single commission engine (Prompt E2). Given an artist's arrangement and a
 * day's sale lines, it works out the envelope the artist is owed and the house
 * share. Merch/aftercare lines are excluded — the studio keeps those whole.
 *
 * READ, DON'T RECOMPUTE: E3's calculations screen reads the payout numbers this
 * engine produced (via the stored PayoutRecord), it never re-runs the maths its
 * own way. This module is the one place the arrangement rules live.
 */

/** A day's commission split for one artist. */
export type CommissionBreakdown = DomainCommissionBreakdown & { arrangement: CommissionArrangement };

/**
 * Resolve the arrangement to apply. An explicit `commissionArrangement` on the
 * artist wins (the per-artist override slot); otherwise it is the percentage
 * default derived from `commissionRate`. The rules live in @atink/domain so the
 * server's day rollover (B1) computes the same envelope.
 */
export function arrangementForArtist(
  artist: Pick<Artist, 'commissionRate' | 'commissionArrangement'>,
): CommissionArrangement {
  return domainArrangementForArtist(artist);
}

/** Compute the day's payout for one arrangement over its sale lines (@atink/domain). */
export function computePayout(
  arrangement: CommissionArrangement,
  lines: readonly SaleLine[],
): CommissionBreakdown {
  return domainComputePayout(arrangement, lines) as CommissionBreakdown;
}

/** Convenience: resolve the artist's arrangement and compute in one call. */
export function computePayoutForArtist(
  artist: Pick<Artist, 'commissionRate' | 'commissionArrangement'>,
  lines: readonly SaleLine[],
): CommissionBreakdown {
  return computePayout(arrangementForArtist(artist), lines);
}
